import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { Mail, Phone, MapPin, Send, Check, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ContactSection = () => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        setIsSubmitted(true);
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({ name: '', email: '', phone: '', message: '' });
        }, 5000);
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      
      let errorMessage = 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.';
      
      if (err.response?.data?.detail) {
        if (typeof err.response.data.detail === 'string') {
          errorMessage = err.response.data.detail;
        } else if (Array.isArray(err.response.data.detail)) {
          errorMessage = err.response.data.detail.map(e => e.msg || JSON.stringify(e)).join(', ');
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="kontakt" ref={ref} className="py-12 sm:py-24 bg-gray-50 relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-gradient-to-bl from-red-100 to-transparent opacity-40 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          className="text-center mb-8 sm:mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 sm:mb-6">
            Kontakt
            <span className="block bg-gradient-to-r from-red-600 to-red-800 bg-clip-text text-transparent">
              aufnehmen
            </span>
          </h2>
          <p className="text-base sm:text-xl text-gray-700 max-w-3xl mx-auto px-2">
            Wir freuen uns auf Ihre Anfrage
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <div className="bg-white rounded-xl sm:rounded-2xl p-5 sm:p-8 shadow-xl">
              <h3 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-gray-900">Senden Sie uns eine Nachricht</h3>
              
              {isSubmitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex flex-col items-center justify-center py-8 sm:py-12"
                >
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-green-500 rounded-full flex items-center justify-center mb-4">
                    <Check className="text-white" size={32} />
                  </div>
                  <h4 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Vielen Dank!</h4>
                  <p className="text-gray-600 text-center text-sm sm:text-base">Wir melden uns in Kürze bei Ihnen.</p>
                </motion.div>
              ) : (
                <>
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-50 border-l-4 border-red-600 rounded"
                    >
                      <div className="flex items-start space-x-2 sm:space-x-3">
                        <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={18} />
                        <p className="text-red-800 text-xs sm:text-sm">{error}</p>
                      </div>
                    </motion.div>
                  )}
                  
                  <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg focus:border-red-600 focus:outline-none transition-colors text-base"
                        placeholder="Ihr Name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">E-Mail *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg focus:border-red-600 focus:outline-none transition-colors text-base"
                        placeholder="ihre@email.de"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">Telefon</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg focus:border-red-600 focus:outline-none transition-colors text-base"
                        placeholder="+49 123 456789"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">Nachricht *</label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows="4"
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg focus:border-red-600 focus:outline-none transition-colors resize-none text-base"
                        placeholder="Beschreiben Sie Ihr Projekt..."
                      ></textarea>
                    </div>

                    <motion.button
                      type="submit"
                      disabled={isSubmitting}
                      className={`w-full px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-red-600 to-red-800 text-white text-base sm:text-lg font-bold rounded-lg shadow-lg ${
                        isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                      whileHover={!isSubmitting ? { scale: 1.02 } : {}}
                      whileTap={!isSubmitting ? { scale: 0.98 } : {}}
                    >
                      <span className="flex items-center justify-center space-x-2">
                        <span>{isSubmitting ? 'Wird gesendet...' : 'Nachricht senden'}</span>
                        {!isSubmitting && <Send size={18} />}
                      </span>
                    </motion.button>
                  </form>
                </>
              )}
            </div>
          </motion.div>

          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-4 sm:space-y-6"
          >
            <div className="bg-white rounded-xl sm:rounded-2xl p-5 sm:p-8 shadow-xl">
              <h3 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-gray-900">Kontaktdaten</h3>
              
              <div className="space-y-3 sm:space-y-4">
                <ContactInfo icon={<Phone />} title="Telefon" content="0911 - 980 12 850" link="tel:+4991198012850" />
                <ContactInfo icon={<Phone />} title="Mobil" content="+49 173 3666776" link="tel:+491733666776" />
                <ContactInfo icon={<Mail />} title="E-Mail" content="info@mp-frankendach.de" link="mailto:info@mp-frankendach.de" />
                <ContactInfo icon={<MapPin />} title="Adresse" content="Schwabacher Str. 260, Fürth" />
              </div>
            </div>

            {/* Business Hours */}
            <div className="bg-gradient-to-br from-gray-900 via-black to-red-950 rounded-xl sm:rounded-2xl p-5 sm:p-8 text-white relative overflow-hidden">
              <div className="absolute inset-0 opacity-20 mix-blend-overlay" style={{
                backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg"%3E%3Cfilter id="noise"%3E%3CfeTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="4" stitchTiles="stitch"/%3E%3C/filter%3E%3Crect width="100%25" height="100%25" filter="url(%23noise)"/%3E%3C/svg%3E")'
              }}></div>

              <div className="relative z-10">
                <h3 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Sprechzeiten</h3>
                <div className="space-y-2 sm:space-y-3 text-sm sm:text-base">
                  <div className="flex justify-between">
                    <span className="text-gray-300">Mo - Do</span>
                    <span className="font-semibold">08:00 - 17:00</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-300">Freitag</span>
                    <span className="font-semibold">08:00 - 13:00</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-300">Sa & So</span>
                    <span className="font-semibold text-red-400">Geschlossen</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const ContactInfo = ({ icon, title, content, link }) => (
  <motion.div
    className="flex items-center space-x-3 sm:space-x-4 p-3 sm:p-4 bg-gray-50 rounded-lg"
    whileHover={{ x: 3 }}
  >
    <div className="p-2 sm:p-3 bg-gradient-to-br from-red-600 to-red-800 rounded-lg flex-shrink-0">
      {React.cloneElement(icon, { size: 20, className: 'text-white sm:w-6 sm:h-6' })}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs sm:text-sm text-gray-500">{title}</p>
      {link ? (
        <a href={link} className="text-sm sm:text-base font-semibold text-gray-900 hover:text-red-600 transition-colors truncate block">
          {content}
        </a>
      ) : (
        <p className="text-sm sm:text-base font-semibold text-gray-900 truncate">{content}</p>
      )}
    </div>
  </motion.div>
);
